import { useCallback, useEffect, useMemo, useState } from 'react'
import { FiscalType } from 'src/api/api.generated'
import { Select as MuiSelect } from '@mui/material'
import { ListSubheader, MenuItem, Stack, Select as VaporSelect } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import { CashDeskTypeOption } from './cash-desk.types'
import { isCashDeskDisabled } from './cash-desk-utils'
import { HICLoading } from 'hic-components'
const Select = VaporSelect as unknown as typeof MuiSelect

export type CashDeskInputProps = {
  cashDesksData: CashDeskTypeOption[]
  value: string | null
  onChange: (value: string | null) => void
  fiscalType?: FiscalType
  error?: boolean
  loading?: boolean
  disabled?: boolean
}

export default function CashDeskInput({ cashDesksData, fiscalType, value, onChange, error, loading, disabled }: CashDeskInputProps) {
  const { t } = useTranslation()

  const [localValue, setLocalValue] = useState<string>('')

  const onLocalChange = useCallback(
    (newLocalValue: string) => {
      if (newLocalValue) {
        const newCashDeskId = decodeCashDeskValue(newLocalValue)?.cashDeskId
        if (newLocalValue !== localValue) {
          setLocalValue(newLocalValue)
        }
        const newValue = newCashDeskId ? newCashDeskId : null
        if (newValue !== value) {
          onChange(newValue)
        }
      }
    },
    [localValue, value],
  )

  useEffect(() => {
    const availableCashDesk = [...cashDesksData]
    if (availableCashDesk.length === 1 && availableCashDesk[0].cashes.length === 1) {
      onLocalChange(encodeCashDeskValue(availableCashDesk[0].cashes[0].id, 0))
    } else if (value === null) {
      onLocalChange('')
    } else {
      let newLocalValue = ''
      let fiscalConfigurationIndex = undefined

      availableCashDesk.every((g, index) => {
        const found = g.cashes.find((c) => c.id === value)?.id
        if (found) {
          newLocalValue = found
          fiscalConfigurationIndex = index
          return false
        }
        return true
      })

      onLocalChange(fiscalConfigurationIndex || fiscalConfigurationIndex === 0 ? encodeCashDeskValue(newLocalValue, fiscalConfigurationIndex) : newLocalValue)
    }
  }, [value, fiscalType, cashDesksData])

  const options = useMemo(() => {
    const result: JSX.Element[] = []
    if (cashDesksData.length > 0)
      cashDesksData.forEach((cg, index) => {
        result.push(<ListSubheader key={index}> {cg.cashFiscalConfiguration.name}</ListSubheader>)
        cg.cashes.map((c) => {
          const isDisabled = isCashDeskDisabled(c, fiscalType)
          result.push(
            <MenuItem key={c.id} value={encodeCashDeskValue(c.id, index)} disabled={isDisabled}>
              {c.cashDeskType.name}
            </MenuItem>,
          )
        })
      })
    else result.push(<MenuItem disabled>{t('pms.cash_desk_input.no_cash_desk_available')}</MenuItem>)
    return result
  }, [cashDesksData, fiscalType])

  const selectedValueFormatted = (value: string) => {
    if (value) {
      const decodedValue = decodeCashDeskValue(value)
      const selectedGroup = decodedValue?.fiscalConfigurationIndex || decodedValue?.fiscalConfigurationIndex === 0 ? cashDesksData[decodedValue.fiscalConfigurationIndex] : undefined
      const selectedCashDesk = selectedGroup?.cashes.find((c) => c.id === decodedValue?.cashDeskId) ?? undefined
      return (
        <Stack sx={{ fontSize: 10, height: '100%', justifyContent: 'space-evenly' }}>
          <span style={{ lineHeight: '10px' }}>{selectedGroup?.cashFiscalConfiguration.name}</span>
          <strong style={{ lineHeight: '12px', fontSize: '12px' }}>{selectedCashDesk?.cashDeskType.name}</strong>
        </Stack>
      )
    }
  }

  return (
    <>
      {loading && <HICLoading />}

      {disabled && localValue ? (
        selectedValueFormatted(localValue)
      ) : (
        <Select<string> //
          disabled={loading || disabled}
          fullWidth
          value={localValue ?? undefined}
          error={error}
          onChange={(event) => onLocalChange(event.target.value)}
          renderValue={(value) => selectedValueFormatted(value)}
        >
          {options}
        </Select>
      )}
    </>
  )
}

const encodeCashDeskValue = (cashDeskId: string, fiscalConfigurationIndex: number): string => {
  return `${cashDeskId}#${fiscalConfigurationIndex}`
}

const decodeCashDeskValue = (cashDeskValue: string): { cashDeskId: string; fiscalConfigurationIndex: number } | undefined => {
  const splitted = cashDeskValue.split('#')
  if (splitted.length === 2) return { cashDeskId: splitted[0], fiscalConfigurationIndex: +splitted[1] }
  return undefined
}
